import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { BackendService } from '../../../core/backend.service';
import { StaticLookupService } from '../../../core/static-lookups/static-lookup.service';
import { DropdownItem } from '../../../viewmodels/dropdown-item';
import { Business } from '../../../viewmodels/industry-hierarchy/business-item';
import { Installation } from '../../../viewmodels/industry-hierarchy/installation-item';
import { PlantUnit } from '../../../viewmodels/industry-hierarchy/plant-unit-item';
import { SystemSection } from '../../../viewmodels/industry-hierarchy/system-section-item';
import { IndustryHierarchySelection, Model } from '../../../viewmodels/model';
import { DynamicFormControlService } from '../../dynamic-forms/dynamic-form-control.service';
import { AutoCompleteControl } from '../../dynamic-forms/form-models/autocomplete-control';
import { AutoCompleteDoubleControl } from '../../dynamic-forms/form-models/autocomplete-double-control';
import { FormControlBase } from '../../dynamic-forms/form-models/form-control-base';
import { PropertyPath } from '../../dynamic-forms/form-models/property-path';
import { FailureRateSelectionService } from '../../equipment-functions/failure-rate-selection.service';

@Component({
  selector: 'hb-industry-hierarchy',
  templateUrl: './industry-hierarchy.component.html',
  styleUrls: ['./industry-hierarchy.component.scss'],
})
export class IndustryHierarchyComponent implements OnInit, OnDestroy {
  private _model: IndustryHierarchySelection;

  get model(): IndustryHierarchySelection {
    return this._model;
  }

  @Input() set model(value: IndustryHierarchySelection) {
    const isChanged = !this._model && value !== this._model;

    this._model = value;

    if (isChanged) {
      this.initializeControls();
    }
  }

  @Input() formGroup: FormGroup;

  private _showIndustryHierarchy: boolean;

  get showIndustryHierarchy(): boolean {
    return this._showIndustryHierarchy;
  }

  @Input() set showIndustryHierarchy(value: boolean) {
    this._showIndustryHierarchy = value;
    this.setIndustryHierarchyVisibility();
  }

  private _isEditable: boolean = true;

  get isEditable(): boolean {
    return this._isEditable;
  }

  @Input() set isEditable(value: boolean) {
    this._isEditable = value;
    this.setState();
  }

  @Input() allowUserEntry: boolean = false;

  @Input() showIndustry: boolean = true;

  @Output() selectionsChanged = new EventEmitter();
  @Output() isoHierarchySummaryChanged = new EventEmitter(true);

  private businessList: Business[] = [];
  private installationList: Installation[] = [];
  private plantUnitList: PlantUnit[] = [];
  private systemSectionList: SystemSection[] = [];
  private isoHierarchySummaryText: string;
  private industryId: string;
  private allItemName: string = this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.All');
  private allItemLabel: string = this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.All');
  private readonly allItemValue = undefined;

  private unsubscribe: Subject<boolean> = new Subject();

  readonly systemSectionInputDebounceTime: number = 500;

  industryDropdownControl = new AutoCompleteControl({
    key: 'industry',
    formControlId: 'industryInput',
    label: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.Industry'),
    required: false,
    propertyName: PropertyPath.get((m: Model) => m.industry.industryName),
    disabled: !this.isEditable,
    visible: true,
    defaultValue: this.allItemName,
  });

  businessInputControl = new AutoCompleteControl({
    key: 'business',
    formControlId: 'businessInput',
    label: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.Business'),
    propertyName: PropertyPath.get((m: Model) => m.industryHierarchyItem.businessItem.name),
    visible: false,
    defaultValue: this.allItemName,
  });

  installationInputControl = new AutoCompleteControl({
    key: 'installation',
    formControlId: 'installationInput',
    label: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.Installation'),
    propertyName: PropertyPath.get((m: Model) => m.industryHierarchyItem.installationItem.name),
    visible: false,
    defaultValue: this.allItemName,
  });

  plantUnitInputControl = new AutoCompleteControl({
    key: 'plantunit',
    formControlId: 'plantUnitInput',
    label: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.PlantUnit'),
    propertyName: PropertyPath.get((m: Model) => m.industryHierarchyItem.plantUnitItem.name),
    visible: false,
    defaultValue: this.allItemName,
  });

  systemSectionInputControl = new AutoCompleteDoubleControl({
    key: 'systemsection',
    leftFormControlId: 'systemInput',
    rightFormControlId: 'sectionInput',
    label: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.SystemSection'),
    propertyName: PropertyPath.get((m: Model) => m.industryHierarchyItem.systemSectionItem),
    leftKey: 'code',
    leftPlaceholder: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.Code'),
    leftFieldName: 'code',
    rightPlaceholder: this.i18NextPipe.transform('IsoHierarchy.IndustryHierarchy.Name'),
    rightFieldName: 'name',
    valueFieldName: 'id',
    visible: false,
    defaultValue: this.allItemName,
  });

  private controls: FormControlBase<any>[] = [
    this.industryDropdownControl,
    this.businessInputControl,
    this.installationInputControl,
    this.plantUnitInputControl,
    this.systemSectionInputControl,
  ];

  isLoaded: boolean;

  constructor(
    private backendService: BackendService,
    private lookupService: StaticLookupService,
    private dynamicFormControlService: DynamicFormControlService,
    private failureRateSelectionService: FailureRateSelectionService,
    private i18NextPipe: I18NextPipe,
  ) {
    this.industryDropdownControl.allowBrowserAutocomplete = false;
    this.businessInputControl.allowBrowserAutocomplete = false;
    this.installationInputControl.allowBrowserAutocomplete = false;
    this.plantUnitInputControl.allowBrowserAutocomplete = false;
    this.systemSectionInputControl.allowBrowserAutocomplete = false;
  }
  ngOnInit() {
    this.dynamicFormControlService.addControls(this.formGroup, this.controls);

    this.initializeIndustryHierarchyControls();
    if (!this.model || !this.formGroup) {
      return;
    }

    this.industryDropdownControl.allowUserEntry = this.allowUserEntry;
    this.businessInputControl.allowUserEntry = this.allowUserEntry;
    this.installationInputControl.allowUserEntry = this.allowUserEntry;
    this.plantUnitInputControl.allowUserEntry = this.allowUserEntry;
    this.systemSectionInputControl.allowUserEntry = this.allowUserEntry;

    this.dynamicFormControlService.setModel(this.model, this.controls);

    this.initIndustryHierarchyOnModelChanged();

    this.validateControlHierarchy();

    this.isLoaded = true;

    this.failureRateSelectionService.invalidCriteriaSelected.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.dynamicFormControlService.setModel(this.model, this.controls);
    });

    this.failureRateSelectionService.selectionCriteriaChanged.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.dynamicFormControlService.setModel(this.model, this.controls);
      this.initIndustryHierarchyOnModelChanged();
      this.validateControlHierarchy();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  /**
   * Validates all controls in the list recursively.
   */
  private validateControlHierarchy() {
    if (!this.isEditable) {
      return;
    }

    for (let i = 0; i < this.controls.length; i++) {
      const control = this.controls[i];
      if (!control.value || control.value === this.allItemName) {
        for (let j = i + 1; j < this.controls.length; j++) {
          this.controls[j].disabled = true;
          this.controls[j].value = this.allItemName;
        }
      } else {
        if (i + 1 < this.controls.length) {
          this.controls[i + 1].disabled = false;
        }
      }
    }
  }

  /**
   * Consumed externally in model-details component to show validation messages to the user.
   *
   * Should be moved to an extenal Validator.
   */
  validateSystemSection() {
    if (!this.model) {
      return false;
    }
    const sysSect: SystemSection = this.model.industryHierarchyItem.systemSectionItem;
    const isCodeEmpty = !sysSect.code || sysSect.code === this.allItemName;
    const isNameEmpty = !sysSect.name || sysSect.name === this.allItemName;
    if ((!isCodeEmpty && isNameEmpty) || (isCodeEmpty && !isNameEmpty)) {
      return false;
    }
    if (isCodeEmpty && isNameEmpty) {
      return true;
    }

    if (!sysSect.id) {
      if (this.systemSectionList.some(it => it.code === sysSect.code || it.name === sysSect.name)) {
        return false;
      }
    }
    return true;
  }

  private initializeIndustryHierarchyControls() {
    this.industryDropdownControl.onChange.pipe(takeUntil(this.unsubscribe)).subscribe((chosenItem: DropdownItem) => {
      this.model.industry.industryId = chosenItem.value;
      this.model.industry.industryName = chosenItem.label;

      this.loadBusinesses(chosenItem.value);
      this.setBusinessToAll();
      this.setInstallationToAll();
      this.setPlantUnitToAll();
      this.setSystemSectionToAll();
      this.selectionsChanged.emit();

      this.industryId = chosenItem.value;
    });

    this.industryDropdownControl.onValueChanged.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      this.setState();
    });

    this.industryDropdownControl.onBlur.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      if (!this.industryDropdownControl.value) {
        this.setIndustryToAll();
      }
    });

    this.businessInputControl.onChange.pipe(takeUntil(this.unsubscribe)).subscribe((chosenItem: DropdownItem) => {
      this.model.industryHierarchyItem.businessItem.id = chosenItem.value;
      this.model.industryHierarchyItem.businessItem.name = chosenItem.label;
      this.model.industryHierarchyItem.businessItem.industryId = this.industryId;

      this.loadInstallations(chosenItem.value);
      this.setInstallationToAll();
      this.setPlantUnitToAll();
      this.setSystemSectionToAll();
      this.selectionsChanged.emit();
    });

    this.businessInputControl.onValueChanged.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      this.setState();
    });

    this.businessInputControl.onBlur.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      if (!this.businessInputControl.value) {
        this.setBusinessToAll();
      }
    });

    this.installationInputControl.onChange.pipe(takeUntil(this.unsubscribe)).subscribe((chosenItem: DropdownItem) => {
      this.model.industryHierarchyItem.installationItem.id = chosenItem.value;
      this.model.industryHierarchyItem.installationItem.name = chosenItem.label;
      this.model.industryHierarchyItem.installationItem.businessId = this.model.industryHierarchyItem.businessItem.id;

      this.loadPlantUnits(chosenItem.value);
      this.setPlantUnitToAll();
      this.setSystemSectionToAll();
      this.selectionsChanged.emit();
    });

    this.installationInputControl.onValueChanged.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      this.setState();
    });

    this.installationInputControl.onBlur.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      if (!this.installationInputControl.value) {
        this.setInstallationToAll();
      }
    });

    this.plantUnitInputControl.onChange.pipe(takeUntil(this.unsubscribe)).subscribe((chosenItem: DropdownItem) => {
      this.model.industryHierarchyItem.plantUnitItem.id = chosenItem.value;
      this.model.industryHierarchyItem.plantUnitItem.name = chosenItem.label;
      this.model.industryHierarchyItem.plantUnitItem.installationId = this.model.industryHierarchyItem.installationItem.id;

      this.loadSystemSections(chosenItem.value);
      this.setSystemSectionToAll();
      this.selectionsChanged.emit();
    });

    this.plantUnitInputControl.onValueChanged.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      this.setState();
    });

    this.plantUnitInputControl.onBlur.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      if (!this.plantUnitInputControl.value) {
        this.setPlantUnitToAll();
      }
    });

    this.systemSectionInputControl.onChange
      .pipe(debounceTime(this.systemSectionInputDebounceTime), takeUntil(this.unsubscribe))
      .subscribe((chosenItem: SystemSection) => {
        this.model.industryHierarchyItem.systemSectionItem.id = chosenItem.id;
        this.model.industryHierarchyItem.systemSectionItem.code = chosenItem.code;
        this.model.industryHierarchyItem.systemSectionItem.name = chosenItem.name;
        this.model.industryHierarchyItem.systemSectionItem.plantUnitId = this.model.industryHierarchyItem.plantUnitItem.id;
        this.selectionsChanged.emit();
      });

    this.loadAllIndustries();
    this.loadAllBusinesses();
    this.loadAllInstallations();
    this.loadAllPlantUnits();
    this.loadAllSystemSections();
  }

  private setSystemSectionToAll() {
    this.systemSectionInputControl.leftValue = this.allItemName;
    this.systemSectionInputControl.rightValue = this.allItemName;
    this.systemSectionInputControl.value = this.allItemName;
    this.model.industryHierarchyItem.systemSectionItem.id = this.allItemValue;
    this.model.industryHierarchyItem.systemSectionItem.code = this.allItemName;
    this.model.industryHierarchyItem.systemSectionItem.name = this.allItemName;
    this.systemSectionInputControl.updateValues();
  }

  private setPlantUnitToAll() {
    this.plantUnitInputControl.value = this.allItemName;
    this.model.industryHierarchyItem.plantUnitItem.id = this.allItemValue;
    this.model.industryHierarchyItem.plantUnitItem.name = this.allItemName;
  }

  private setInstallationToAll() {
    this.installationInputControl.value = this.allItemName;
    this.model.industryHierarchyItem.installationItem.id = this.allItemValue;
    this.model.industryHierarchyItem.installationItem.name = this.allItemName;
  }

  private setBusinessToAll() {
    this.businessInputControl.value = this.allItemName;
    this.model.industryHierarchyItem.businessItem.id = this.allItemValue;
    this.model.industryHierarchyItem.businessItem.name = this.allItemName;
  }

  private setIndustryToAll() {
    this.industryDropdownControl.value = this.allItemName;
    this.model.industry.industryId = this.allItemValue;
    this.model.industry.industryName = this.allItemName;
  }

  private initIndustryHierarchyOnModelChanged() {
    this.industryId = this.model.industry ? this.model.industry.industryId : '';

    this.loadAllIndustries();
    this.loadAllBusinesses();
    this.loadAllInstallations();
    this.loadAllPlantUnits();
    this.loadAllSystemSections();

    this.isoHierarchySummaryChanged.emit();
  }

  private loadAllIndustries() {
    this.lookupService
      .getById('Industry', true)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        if (!result) {
          return;
        }

        this.industryDropdownControl.options = result.map(it => {
          if (it.key) {
            return new DropdownItem(it.value, it.key);
          } else {
            return new DropdownItem(this.allItemLabel, this.allItemValue);
          }
        });
      });
  }

  private loadBusinesses(parentId) {
    const collection = [];
    collection.push(new DropdownItem(this.allItemLabel, this.allItemValue));
    if (!this.businessList || !this.businessList.length) {
      this.businessInputControl.options = collection;
      return;
    }

    let businesses: DropdownItem[];

    if (parentId) {
      businesses = _.filter(this.businessList, it => it.industryId === parentId).map(it => new DropdownItem(it.name, it.id));
    }

    this.businessInputControl.options = collection.concat(this.sortOptions(businesses));
  }

  private loadAllBusinesses() {
    this.backendService.getAll<Business>(Business).subscribe(item => {
      if (!item) {
        return;
      }

      this.businessList = item;
      this.loadBusinesses(this.industryId);
    });
  }

  private loadInstallations(parentId) {
    const collection = [];
    collection.push(new DropdownItem(this.allItemLabel, this.allItemValue));
    if (!this.installationList || !this.installationList.length) {
      this.installationInputControl.options = collection;
      return;
    }

    let installations: DropdownItem[];

    if (parentId) {
      installations = _.filter(this.installationList, it => it.businessId === parentId).map(it => new DropdownItem(it.name, it.id));
    }

    this.installationInputControl.options = collection.concat(this.sortOptions(installations));
  }

  private loadAllInstallations() {
    this.backendService.getAll<Installation>(Installation).subscribe(item => {
      if (!item) {
        return;
      }

      this.installationList = item;
      if (this.model) {
        this.loadInstallations(this.model.industryHierarchyItem.businessItem.id);
      }
    });
  }

  private loadPlantUnits(parentId) {
    const collection = [];
    collection.push(new DropdownItem(this.allItemLabel, this.allItemValue));
    if (!this.plantUnitList || !this.plantUnitList.length) {
      this.plantUnitInputControl.options = collection;
      return;
    }

    let plantUnits: DropdownItem[];

    if (parentId) {
      plantUnits = _.filter(this.plantUnitList, it => it.installationId === parentId).map(it => new DropdownItem(it.name, it.id));
    }

    this.plantUnitInputControl.options = collection.concat(this.sortOptions(plantUnits));
  }

  private loadAllPlantUnits() {
    this.backendService.getAll<PlantUnit>(PlantUnit).subscribe(item => {
      if (!item) {
        return;
      }

      this.plantUnitList = item;
      if (this.model) {
        this.loadPlantUnits(this.model.industryHierarchyItem.installationItem.id);
      }
    });
  }

  private loadSystemSections(parentId) {
    const collection = [];
    const allSystemSectionItem = new SystemSection();
    allSystemSectionItem.id = this.allItemValue;
    allSystemSectionItem.name = this.allItemName;
    allSystemSectionItem.code = this.allItemName;
    collection.push(allSystemSectionItem);
    if (!this.systemSectionList || !this.systemSectionList.length) {
      this.systemSectionInputControl.options = collection;
      return;
    }

    let systemSections: SystemSection[];

    if (parentId) {
      systemSections = _.filter(this.systemSectionList, it => it.plantUnitId === parentId);
    }

    this.systemSectionInputControl.options = collection.concat(
      _.orderBy(systemSections, (systemSection: SystemSection) => systemSection.name, 'asc'),
    );
  }

  private loadAllSystemSections() {
    this.backendService.getAll<SystemSection>(SystemSection).subscribe(item => {
      if (!item) {
        return;
      }

      this.systemSectionList = item;
      if (this.model) {
        this.loadSystemSections(this.model.industryHierarchyItem.plantUnitItem.id);
      }
    });
  }

  private setState() {
    const isBusinessEnabled = !!this.industryDropdownControl.value && this.industryDropdownControl.value !== this.allItemName && this.isEditable;
    const isInstallationEnabled =
      isBusinessEnabled && !!this.businessInputControl.value && this.businessInputControl.value !== this.allItemName && this.isEditable;
    const isPlantUnitEnabled =
      isInstallationEnabled && !!this.installationInputControl.value && this.installationInputControl.value !== this.allItemName && this.isEditable;
    const isSystemSectionEnabled =
      isPlantUnitEnabled && !!this.plantUnitInputControl.value && this.plantUnitInputControl.value !== this.allItemName && this.isEditable;

    this.industryDropdownControl.disabled = !this.isEditable;
    this.businessInputControl.disabled = !isBusinessEnabled;
    this.installationInputControl.disabled = !isInstallationEnabled;
    this.plantUnitInputControl.disabled = !isPlantUnitEnabled;
    this.systemSectionInputControl.disabled = !isSystemSectionEnabled;
  }

  private setIndustryHierarchyVisibility() {
    // TODO: Should Industry be in this list to fix bug on Model Details page?
    this.businessInputControl.visible = this._showIndustryHierarchy;
    this.installationInputControl.visible = this._showIndustryHierarchy;
    this.plantUnitInputControl.visible = this._showIndustryHierarchy;
    this.systemSectionInputControl.visible = this._showIndustryHierarchy;
  }

  private sortOptions(options: DropdownItem[]): DropdownItem[] {
    return _.orderBy(options, (item: DropdownItem) => item.label, 'asc');
  }

  private initializeControls(): any {
    this.dynamicFormControlService.setModel(this.model, this.controls);
  }
}
